var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { colors } from 'syf-component-library/defaultTheme';
import Button from 'syf-component-library/ui/atoms/Button';
import { Icon } from '../AlmeUtilities/Icons/StyledIcon';
const Input = styled.input `
  font-family: inherit;
  font-size: ${props => props.theme.fontSizes.default};
  width: 100%;
  border: none !important;
  box-shadow: none !important;
  background: none;
  color: ${colors.black};
  padding: 0;
  flex-grow: 1;
  &:focus {
    outline: none !important;
    border: none !important;
    color: ${colors.black};
  }
  &:hover {
    border: none !important;
    outline: none !important;
  }
  `;
const InputWrapper = styled.div `
  color: ${colors.heavyGrey};
  width: 100%;
  height: 48px;
  border-radius: 6px;
  padding: 0 5px 0 5px;
  box-shadow: 0 0 0 1px ${colors.heavyGrey};
  display: flex;
  align-items: center;
  flex-grow: 1;
  background-color: white;
  &:hover {
    box-shadow: 0 0 0 2px ${colors.heavyGrey};
  }

  cursor: 'text';
  &:focus-within  {
    box-shadow: 0 0 0 1px ${(props) => props.theme.field.focus};
  }
`;
const MessageBoxWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content:center;
  flex-grow: 1;
  background-color: #e6e6e6;
`;
const ControlWrapper = styled.div `
  display: flex;
  align-items: center;
  background-color: #e6e6e6;
`;
const CharLimitDisplay = styled.div `
  text-align:left;
  color: #4A4A4A;
  font-size: ${props => props.theme.fontSizes.subText};
  flex-grow:1;
  background-color:inherit;
  margin-top:5px;
  padding-right: 2px;
`;
const StyledButton = styled(Button) `
  margin: 0 0 0 8px;
  min-width: 100px;  
`;
const ButtonText = styled.span `
  display: inline-block;
  margin-left: 8px;
`;
function getPlaceHolderText(disabled) {
    return disabled ? "Please see response" : "";
}
const MessageInputBox = (_a) => {
    var { id, onSend, onSendTypingEvent, maxTextLength } = _a, props = __rest(_a, ["id", "onSend", "onSendTypingEvent", "maxTextLength"]);
    const [userInput, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const inputRef = useRef(null);
    const userTypingEventDelay = props.almeAppConfig.footer.userTypingEventDelay || 10000;
    useEffect(() => {
        let timer;
        if (isTyping) {
            timer = window.setTimeout(() => {
                setIsTyping(false);
            }, userTypingEventDelay);
        }
        return () => {
            timer && clearTimeout(timer);
        };
    }, [isTyping, userTypingEventDelay]);
    const textChanged = (event) => {
        let inputText = event.target.value;
        if (maxTextLength && inputText.length > maxTextLength) {
            inputText = inputText.substring(0, maxTextLength);
        }
        if (props.isLiveChatConnected && typeof onSendTypingEvent === 'function' && !isTyping) {
            setIsTyping(true);
            onSendTypingEvent();
        }
        setInput(inputText);
    };
    const onSubmitInput = () => {
        const inputText = userInput.trim();
        if (inputText) {
            onSend(inputText);
            setInput('');
        }
    };
    const onKeyDown = (event) => {
        // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            onSubmitInput();
        }
    };
    const inputProps = {
        id,
        'aria-label': props.ariaLabel,
        name: id,
        value: userInput,
        onChange: textChanged,
        onKeyDown,
        maxLength: maxTextLength,
        placeholder: getPlaceHolderText(props.disabled),
        inputClassName: props.inputClassName,
        readOnly: props.disabled,
        tabIndex: props.tabIndex,
    };
    const buttonProps = {
        sendButtonLabel: props.sendButtonLabel,
        disabled: props.disabled,
        tabIndex: props.tabIndex,
    };
    return (React.createElement(MessageBoxWrapper, null,
        React.createElement(ControlWrapper, null,
            React.createElement(InputWrapper, { onClick: () => inputRef.current && inputRef.current.focus() },
                React.createElement(Input, Object.assign({ ref: inputRef }, inputProps))),
            React.createElement(StyledButton, Object.assign({ id: 'alme-message-submit-button', onClick: onSubmitInput, buttonType: 'primary', type: 'button' }, buttonProps),
                React.createElement(React.Fragment, null,
                    React.createElement(Icon, { size: 16, iconType: "SendIcon" }),
                    React.createElement(ButtonText, null, props.sendButtonLabel || 'Send')))),
        React.createElement(CharLimitDisplay, null, `${userInput.length} of ${maxTextLength} characters`)));
};
export default MessageInputBox;
export function OnSendTypingEvent(apiUtils) {
    return useCallback(async () => {
        await apiUtils.submitReportingEvent('Typing', 'User typing out of sequence.');
    }, [apiUtils]);
}
